import { BlankSlate, Box, IconBadge, Title } from '@factorialco/deprecated-design-system'
import {
  AllocationFragment,
  ExpensableAlertsDocument,
  ExpensableBudgetFragment,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  DuplicateExpenseAlert,
  isDuplicateExpenseAlert,
} from 'modules/expenses/hooks/useExpenseAlerts'
import {
  budgetAlertBadge,
  policyAlertBadge,
  syncAlertBadge,
} from 'modules/expenses/lib/budgets/alerts'
import { isDraftExpensable, isManualExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

import AllocationStatusCard from './_AllocationStatusCard'
import AlertsBlankState from './_BlankState'
import BudgetStatusCard from './_BudgetStatusCard'
import { DuplicateExpensesCard } from './_DuplicateExpensesCard'
import MileageStatusCard from './_MileageStatusCard'
import PolicyStatusCard from './_PolicyStatusCard'
import SyncStatusCard from './_SyncStatusCard'

interface Props {
  expensable: Expensable
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment
  policiesResultData?: SpendingPolicesResultData
  canSeeBudgets: boolean
  canCreateBudgets?: boolean
  canCreateSpendingPolicies?: boolean
  spendingAlerts?: SpendingAlertFragment[]
  canOverrideApprovalFlow?: boolean
  expensesPolicySettingsEnabled?: boolean
}

const Alerts = ({
  expensable,
  budget,
  budgetAllocation,
  policiesResultData,
  canSeeBudgets,
  canCreateBudgets = false,
  canCreateSpendingPolicies = false,
  spendingAlerts,
  canOverrideApprovalFlow,
  expensesPolicySettingsEnabled = false,
}: Props) => {
  const syncableState = expensable?.syncableState?.[0] ?? null
  const budgetAlert = budgetAlertBadge(budget?.currentStatus, budgetAllocation?.status)
  const policyAlert = policyAlertBadge(policiesResultData?.result)
  const syncAlert = syncAlertBadge()
  const isDraftManual = isManualExpensable(expensable) && isDraftExpensable(expensable)
  const showPolicyAlerts =
    !expensesPolicySettingsEnabled &&
    policiesResultData?.result &&
    policiesResultData.result !== 'not_applicable'
  const showBudgetAlerts =
    !expensesPolicySettingsEnabled && !isDraftManual && canSeeBudgets && !!budget

  const mileageRateChangedAlerts =
    spendingAlerts?.filter((alert) => alert.type === 'mileage_rate_changed') || []

  const showMileageAlerts = expensesPolicySettingsEnabled
    ? mileageRateChangedAlerts.length > 0
    : expensable?.mileage?.calculatedMileage &&
      expensable?.mileage?.calculatedMileage !== expensable.mileage.mileage

  const duplicateExpenseAlerts: DuplicateExpenseAlert[] = expensesPolicySettingsEnabled
    ? spendingAlerts?.filter(
        (alert): alert is DuplicateExpenseAlert =>
          (alert.type === 'duplicate_expense' || alert.type === 'per_diem_conflict') &&
          isDuplicateExpenseAlert(alert)
      ) || []
    : spendingAlerts?.filter(isDuplicateExpenseAlert) || []

  const duplicateAlerts = duplicateExpenseAlerts.filter(
    (alert) => alert.type === 'duplicate_expense'
  )
  const perDiemConflictAlerts = duplicateExpenseAlerts.filter(
    (alert) => alert.type === 'per_diem_conflict'
  )

  const showDuplicateAlerts = duplicateAlerts.length > 0
  const showPerDiemConflictAlerts = perDiemConflictAlerts.length > 0
  const showSyncAlerts =
    !!syncableState && (syncableState.status === 'failed' || syncableState.status === 'invalid')
  const { data, loading } = useQuery(ExpensableAlertsDocument, {
    variables: {
      budgetIds: [String(budget?.id)],
      includeBudgets: showBudgetAlerts,
    },
  })
  const budgetSpend = data?.finance?.budgetsConnection.nodes[0]

  const showBlankState =
    !showPolicyAlerts &&
    !showBudgetAlerts &&
    !showMileageAlerts &&
    !showDuplicateAlerts &&
    !showPerDiemConflictAlerts &&
    !showSyncAlerts

  useEffect(() => {
    track(trackingEvents.expenses.checkAlertsTab, { duplicate_alert: showDuplicateAlerts })
  }, [expensable.id, showDuplicateAlerts])

  return (
    <Box padding="s24" gap="s40">
      {showDuplicateAlerts && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge icon={'alert-circle'} color={'radicalRed200'} iconColor={'radicalRed1000'} />
            <Title
              variant="400"
              title={i18n.t('expenses_v2.expense.duplicate_alerts.title', {
                count: duplicateAlerts.length,
              })}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            {duplicateAlerts.length > 0 && (
              <DuplicateExpensesCard
                expensable={expensable}
                type="duplicate_alerts"
                duplicateExpenseAlerts={duplicateAlerts}
                canOverrideApprovalFlow={canOverrideApprovalFlow}
              />
            )}
          </Box>
        </Box>
      )}
      {showPerDiemConflictAlerts && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge icon={'alert-circle'} color={'radicalRed200'} iconColor={'radicalRed1000'} />
            <Title
              variant="400"
              title={i18n.t('expenses_v2.expense.per_diem_conflict_alerts.title', {
                count: perDiemConflictAlerts.length,
              })}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            {perDiemConflictAlerts.length > 0 && (
              <DuplicateExpensesCard
                expensable={expensable}
                type="per_diem_conflict"
                duplicateExpenseAlerts={perDiemConflictAlerts}
                canOverrideApprovalFlow={canOverrideApprovalFlow}
              />
            )}
          </Box>
        </Box>
      )}
      {showPolicyAlerts && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge
              icon={policyAlert.icon}
              color={policyAlert.color}
              iconColor={policyAlert.iconColor}
            />
            <Title
              variant="400"
              title={i18n.t('budgets.forms.external_resource.spending_policy.label')}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            {policiesResultData?.policiesData.map((policy) => (
              <PolicyStatusCard key={policy.name} expensable={expensable} policy={policy} />
            ))}
          </Box>
        </Box>
      )}
      {showBudgetAlerts && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge
              icon={budgetAlert.icon}
              color={budgetAlert.color}
              iconColor={budgetAlert.iconColor}
            />
            <Title
              variant="400"
              title={i18n.t('budgets.forms.external_resource.budget_field.label')}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            {budgetAllocation?.status && (
              <AllocationStatusCard allocation={budgetAllocation} currency={budget.currency} />
            )}
            {!loading && budgetSpend && <BudgetStatusCard budget={budgetSpend} />}
          </Box>
        </Box>
      )}
      {showMileageAlerts && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge icon={'alert-circle'} color={'tangerine200'} iconColor={'tangerine1000'} />
            <Title
              variant="400"
              title={i18n.t('expenses_v2.forms.fields.mileage.alert.manually_changed.summary')}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            <MileageStatusCard mileage={expensable.mileage} />
          </Box>
        </Box>
      )}
      {showSyncAlerts && syncableState && (
        <Box gap="s16">
          <Box gap="s12" flexDirection="row">
            <IconBadge
              icon={syncAlert.icon}
              color={syncAlert.color}
              iconColor={syncAlert.iconColor}
            />
            <Title
              variant="400"
              title={i18n.t('expenses_v2.alerts.integrations.integration_status')}
            />
          </Box>
          <Box gap="s16" paddingLeft="s32">
            <SyncStatusCard syncableState={syncableState} />
          </Box>
        </Box>
      )}
      {showBlankState &&
        (expensesPolicySettingsEnabled ? (
          <BlankSlate
            size="small"
            illustration="check"
            title={i18n.t(
              'budgets.detail.tabs.spendings.sidepanel.alerts.blank_state.default.title'
            )}
            description={i18n.t(
              'budgets.detail.tabs.spendings.sidepanel.alerts.blank_state.default.description'
            )}
          />
        ) : (
          <AlertsBlankState
            canCreateBudgets={canCreateBudgets}
            canCreateSpendingPolicies={canCreateSpendingPolicies}
          />
        ))}
    </Box>
  )
}

export default Alerts
